import type {
  FinanceEntityInvestigation,
  FinanceInvestigationEventsResponse,
  FinanceInvestigationKind,
  FinanceInvestigationSignalsResponse,
} from "@ibada/shared-types/finance"

import { ApiError, apiBaseUrl, buildApiRequestHeaders, http } from "@/shared/lib/api/http"

/** Explorer sort column (the events endpoint's `sortBy`). `Amount` sorts by the stored amount, which is unsigned. */
export enum FinanceInvestigationSortBy {
  OccurredAt = "occurredAt",
  Amount = "amount",
}

export enum FinanceInvestigationSortOrder {
  Asc = "asc",
  Desc = "desc",
}

/** Wire params for the Explorer events query. All optional facets map to real backend filters. */
export type FinanceInvestigationEventsParams = {
  workspaceId: string
  branchId?: string
  scope?: "BRANCH" | "PARISH"
  accountingAccountId?: string
  fundId?: string
  activityId?: string
  budgetLineId?: string
  incomeCategoryId?: string
  expenseCategoryId?: string
  programId?: string
  memberId?: string
  ministryId?: string
  groupId?: string
  departmentId?: string
  actorUserId?: string
  transactionReference?: string
  type?: string
  status?: string
  includeReversals?: boolean
  reconciled?: boolean
  hasEvidence?: boolean
  amountFrom?: number
  amountTo?: number
  dateFrom?: string
  dateTo?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: FinanceInvestigationSortBy
  sortOrder?: FinanceInvestigationSortOrder
}

async function getEvents(params: FinanceInvestigationEventsParams): Promise<FinanceInvestigationEventsResponse> {
  const response = await http.get<FinanceInvestigationEventsResponse>("/finance/investigation/events", { params })
  return response.data
}

async function getDetail(
  kind: FinanceInvestigationKind | string,
  id: string,
  workspaceId: string,
): Promise<FinanceEntityInvestigation> {
  const response = await http.get<FinanceEntityInvestigation>(`/finance/investigation/${kind}/${id}`, {
    params: { workspaceId },
  })
  return response.data
}

async function getSignals(params: {
  workspaceId: string
  branchId?: string
  scope?: "BRANCH" | "PARISH"
}): Promise<FinanceInvestigationSignalsResponse> {
  const response = await http.get<FinanceInvestigationSignalsResponse>("/finance/investigation/signals", { params })
  return response.data
}

const filenameFrom = (disposition: string | null): string | null => {
  if (!disposition) return null
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

/**
 * The investigation as a PDF, in the reader's language. Read with `fetch` rather than axios so a refusal still
 * arrives as the JSON envelope (an axios blob request hides it) and the shared resolver can name the reason.
 */
async function downloadPdf(params: {
  kind: FinanceInvestigationKind | string
  id: string
  workspaceId: string
  locale: string
}): Promise<{ blob: Blob; fileName: string }> {
  const query = new URLSearchParams({ workspaceId: params.workspaceId, locale: params.locale })
  const response = await fetch(
    `${apiBaseUrl}/finance/investigation/${encodeURIComponent(params.kind)}/${encodeURIComponent(params.id)}/pdf?${query}`,
    { headers: await buildApiRequestHeaders() },
  )
  if (!response.ok) {
    let issues: ConstructorParameters<typeof ApiError>[2]
    try {
      issues = ((await response.json()) as { issues?: ConstructorParameters<typeof ApiError>[2] }).issues
    } catch {
      issues = undefined
    }
    throw new ApiError("Investigation PDF failed", response.status, issues)
  }
  return {
    blob: await response.blob(),
    fileName: filenameFrom(response.headers.get("content-disposition")) ?? `enquete-${params.id}.pdf`,
  }
}

export const financeInvestigationApi = {
  getEvents,
  getDetail,
  getSignals,
  downloadPdf,
}
